import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import { Skeleton } from "@mui/material";
import type { ComponentProps } from "react";
import { COPY } from "@/constants/campaignContacts";
import type { RecipientAdditionsQuote } from "@/interfaces/campaignContacts";
import { formatUsd } from "@/utils/number-format";
import AdditionCreditsPurchase from "./AdditionCreditsPurchase";
import { creditLimitNotice, paymentLines } from "./addRecipients-helper";
import StepNotice from "./StepNotice";
import type { AdditionCreditsCopy, PaymentLineLabelOverrides } from "./types";

/** What the wizard wires into the credits purchase; the quote is this step's own. */
type CreditsPurchaseControls = Omit<ComponentProps<typeof AdditionCreditsPurchase>, "quote">;

interface AddRecipientPaymentStepProps {
  /** Absent until the first quote arrives (or when it failed). */
  quote?: RecipientAdditionsQuote;
  /**
   * A quote request is in flight: the first one shows placeholders; a re-quote, including another
   * deposit mode's (the previous figures stay up meanwhile, F6), dims the figures and holds them.
   */
  loading: boolean;
  /** Why the quote could not be loaded. */
  error: string | null;
  /** A refused confirm (B9): the server's sentence, shown under the fresh figures. */
  notice: string | null;
  /** The postpaid Card / Invoice choice and the credits the wallet lacks (R4): buy, wait or ask. */
  creditsPurchase: CreditsPurchaseControls;
  /** Per-line label overrides (the gift links flow words "New gifts" as links); amounts untouched. */
  lineLabels?: PaymentLineLabelOverrides;
  /** Credit-limit wording override; the shortfall wording is the view's (`creditsShortfallView`). */
  creditsCopy?: AdditionCreditsCopy;
}

const SKELETON_LINES = [0, 1, 2, 3] as const;
const SECTION_CLASS =
  "mt-[18px] mb-2.5 text-[11px] font-bold uppercase tracking-[2px] text-dust-ink";

/** One line per figure, from `paymentLines`, the credits still needed included. */
function PaymentLines({
  quote,
  labels,
}: {
  quote: RecipientAdditionsQuote;
  labels?: PaymentLineLabelOverrides;
}) {
  return (
    <dl className="m-0">
      {paymentLines(quote, labels).map((line) => (
        <div
          key={line.key}
          className="flex items-baseline justify-between gap-4 py-[7px] text-[14px] text-graphite"
        >
          <dt>{line.label}</dt>
          <dd className="m-0 text-right font-semibold text-rodeo-black">{line.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** The wallet that funded the campaign funds the additions too; it is shown, never picked. */
function FundingWallet({ quote }: { quote: RecipientAdditionsQuote }) {
  if (quote.paidCount === 0 || quote.walletName === null) return null;
  return (
    <>
      <p className={SECTION_CLASS}>{COPY.addWizard.walletSection}</p>
      <div className="flex items-start gap-2.5 rounded-lg border border-smoke bg-clear-dust px-3.5 py-2.5">
        <AccountBalanceWalletOutlinedIcon
          sx={{ fontSize: 18, mt: "1px", color: "var(--color-dust-ink)" }}
        />
        <div className="min-w-0">
          <strong className="block text-[13.5px] text-rodeo-black">{quote.walletName}</strong>
          <small className="mt-0.5 block text-[12px] text-graphite">
            {COPY.addWizard.walletNote(formatUsd(quote.walletAvailable))}
          </small>
        </div>
      </div>
    </>
  );
}

/**
 * The payment review (B4, B5): the quote's figures, the funding wallet, buying the credits it lacks
 * and every reason the addition can't go ahead. Gating CONTINUE is the wizard's job
 * (`primaryDisabled`).
 */
export default function AddRecipientPaymentStep({
  quote,
  loading,
  error,
  notice,
  creditsPurchase,
  lineLabels,
  creditsCopy,
}: AddRecipientPaymentStepProps) {
  if (quote === undefined) {
    if (!loading) return error === null ? null : <StepNotice>{error}</StepNotice>;
    return (
      <div role="status" aria-busy="true" className="flex flex-col gap-2">
        {SKELETON_LINES.map((line) => (
          <Skeleton key={line} variant="rounded" height={28} />
        ))}
      </div>
    );
  }

  const creditLimit = creditLimitNotice(quote, creditsCopy);
  return (
    <div aria-busy={loading} className={loading ? "opacity-60 transition-opacity" : undefined}>
      <PaymentLines quote={quote} labels={lineLabels} />
      <FundingWallet quote={quote} />
      <AdditionCreditsPurchase
        quote={quote}
        {...creditsPurchase}
        disabled={creditsPurchase.disabled || loading}
      />
      <div className="mt-3.5 flex flex-col gap-2.5 empty:hidden">
        {creditLimit !== null && <StepNotice blocking>{creditLimit}</StepNotice>}
        {error !== null && <StepNotice>{error}</StepNotice>}
        {notice !== null && <StepNotice>{notice}</StepNotice>}
      </div>
    </div>
  );
}
